// Карточка группы: личная (клиенты платят сами) или коммерческий проект (платит организация, счёт общий).
import { COLOR_DEFAULTS, GROUP_KIND, MEET, RU_MONTHS, addMonths, effectiveRules, hasValue, isColor, isCommercial,
  monthOf, money, pad2, plural, ruMonth, tgLink, waLink, type GroupKind, type GroupOut } from '@praktika/core';
import { afterChange, call } from '../lib/api.ts';
import { cdot, colName, colorSheet, colv } from '../lib/colors.ts';
import { $, $$, copyText, esc, toast } from '../lib/dom.ts';
import { activeTypes, balPill, defaultType, groupById, meetLabel, typeName } from '../lib/data.ts';
import { SCREENS, back, go, render, replace, title } from '../lib/nav.ts';
import { chooseSheet, curSheet, kvBtn, kvTog, openSheet, promptSheet } from '../lib/sheet.ts';
import { S, UI, type GroupDraft } from '../lib/state.ts';
import { nowInfo } from '../lib/zones.ts';
import { clientSorter } from './clients.ts';
import { bindCommon, meetSheet } from './common.ts';
import { MESSENGERS, chooseRule, clientInfo, lastMove, ruleText, sortCodes } from './parts.ts';

const KIND_LABEL: Record<GroupKind, string> = { personal: 'личная · клиенты платят сами',
  project: 'коммерческий проект · платит организация' };

SCREENS.group = (app, p) => {
  const isNew = !p.id, ex: GroupOut | null = isNew ? null : groupById(String(p.id));
  const g: GroupDraft | null = isNew ? UI.draftGroup ?? null : ex;
  if (!g) { app.innerHTML = title('Группа') + '<div class="empty">Не найдена</div>'; return; }
  const comm = isCommercial(g), isDef = !!ex?.isDefault;
  const rules = ex ? ex.rules : effectiveRules(null, g, S.settings);
  const save = (patch: Record<string, unknown>) => {
    if (!ex) { Object.assign(g, patch); render(); return; }
    void call('saveGroup', { ...patch, id: ex.id }).then(() => afterChange('Сохранено'), () => {});
  };
  // клиенты группы — в том же порядке, что на вкладке «Группы»
  const cls = ex ? S.clients.filter((c) => c.groupId === ex.id).sort(clientSorter(S.settings.grp_sort || 'name')) : [];
  let html = '<div class="p-title"><button class="back" data-back>‹</button><span class="t">' +
    (isNew ? (comm ? 'Новый проект' : 'Новая группа') : esc(g.name)) + '</span>' +
    (ex && comm ? balPill(ex.balance, ex.cur) : '') + '</div>';
  if (ex && comm) html += '<div class="btnrow"><button class="btn sec" data-report>Отчёт за месяц</button>' +
    '<button class="btn sec" data-write>Написать</button></div>';
  html += '<div class="card">' + kvBtn('Название', g.name, 'data-f="name"') + (isDef ?
    '<div class="kv"><span class="k">Вид</span><span class="v">личная · по умолчанию</span></div>' :
    kvBtn('Вид', KIND_LABEL[g.kind], 'data-kind'));
  if (isDef) html += '<div class="kv col"><span class="v small">Сюда попадают новые клиенты. Цена, тип сессии и правила ' +
    'отмены — из общих настроек и карточек клиентов.</span></div>';
  else html += kvBtn('Цена за сессию', hasValue(g.price) ? money(g.price, g.cur) : '', 'data-price') +
    kvBtn('Тип сессии', g.typeId ? typeName(g.typeId) : (comm ? '' : 'как у клиента'), 'data-type') +
    kvBtn('Встреча', meetLabel(g) || (comm ? '' : 'как у клиента'), 'data-meet');
  if (comm) html += kvBtn('Платит', g.pays === 'monthly' ? (g.payDay || 5) + '-го за прошлый месяц' : 'за каждую сессию',
    'data-pays') + kvBtn('ИНН', g.inn, 'data-f="inn"') + kvTog('Чек выбивает сам', g.selfReceipt, 'data-tog="selfReceipt"') +
    kvBtn('Подписи банка', g.bankNames, 'data-f="bankNames"') + kvBtn('Контакт', g.contact, 'data-f="contact" data-type="tel"') +
    kvBtn('Мессенджер', g.messenger, 'data-msgr');
  if (ex) html += kvBtn('Код в календаре', sortCodes(ex.calCodes).join(', ') || 'нет', 'data-codes');
  html += '<button class="kv" data-color><span class="k">Цвет клиентов группы</span><span class="v chev">' +
    (isColor(g.color) ? colv(g.color) : 'как у всех') + '</span></button>' +
    kvBtn('Заметка', g.note, 'data-f="note" data-type="textarea"') + '</div>';
  if (!isDef) html += '<div class="card"><div class="hd">Правила отмены группы</div>' + kvBtn('Порог «вовремя»',
    rules.hours + ' ч', 'data-rule="hours"') + kvBtn('Отмена поздно', ruleText(rules.late, g.cur), 'data-rule="late"') +
    kvBtn('Не пришёл', ruleText(rules.noshow, g.cur), 'data-rule="noshow"') + '</div>';
  if (!ex) html += '<div class="btnrow"><button class="btn pri" data-create' + (g.name ? '' : ' disabled') +
    '>Сохранить</button></div>';
  else {
    if (comm) html += '<div class="card"><div class="hd">Счёт проекта</div>' + ex.months.map((mm) => {
      const due = ex.pays === 'monthly' ? ' · срок ' + (ex.payDay || 5) + '.' + pad2(Number(addMonths(mm.ym, 1).slice(5, 7))) :
        '';
      return '<div class="kv"><span class="k">' + esc(RU_MONTHS[Number(mm.ym.slice(5, 7)) - 1]) + ' · ' + mm.n + ' ' +
        plural(mm.n, 'сессия', 'сессии', 'сессий') + '</span><span class="v' + (mm.paid >= mm.charged && mm.charged ?
        ' pos' : '') + '">' + esc(money(mm.charged, ex.cur)) + (mm.paid ? ' · получено ' + esc(money(mm.paid, ex.cur)) :
        (mm.charged ? due : '')) + '</span></div>';
    }).join('') + '</div>';
    html += '<div class="list-h">Клиенты группы · ' + cls.length + '</div><div class="p-list">' + (cls.map((c) => {
      const mv = lastMove(c);
      return '<button class="p-row cl" data-client="' + esc(c.id) + '"><div class="who"><div class="n">' + cdot(c) +
        esc(c.name) + '</div><div class="i">' + esc(clientInfo(c)) + '</div>' + (mv ? '<div class="i mv">' + esc(mv) +
        '</div>' : '') + '</div>' + (!comm || c.balance ? balPill(c.balance, c.balanceCur) : '') + '</button>';
    }).join('') || '<div class="empty">Переведите клиентов в эту группу в их карточках: «Группа»</div>') + '</div>';
    if (!isDef) html += '<div class="btnrow" style="margin-top:12px"><button class="btn txt" data-archive>' +
      (ex.archived ? 'Вернуть из архива' : 'В архив') + '</button>' + (!cls.length &&
      !S.sessions.some((x) => x.groupId === ex.id) ? '<button class="btn txt" data-del>Удалить группу</button>' : '') +
      '</div>';
  }
  app.innerHTML = html;
  bindCommon(app);
  $$('[data-client]', app).forEach((b) => { b.onclick = () => go('client', { id: b.getAttribute('data-client') }); });
  const rp = $('[data-report]', app);
  if (rp && ex) rp.onclick = () => chooseSheet('Отчёт за', [0, 1, 2].map((i) => {
    const ym = addMonths(monthOf(nowInfo().date), -i);
    return { value: ym, label: ruMonth(ym) };
  }), '', (ym) => void call<{ text: string; n: number }>('groupReport', ex.id, ym).then((r) => {
    const sh = openSheet('<div class="ttl">Отчёт для «' + esc(ex.name) + '»</div><p class="sub">' + esc(ruMonth(ym)) +
      ' · ' + r.n + ' ' + plural(r.n, 'сессия', 'сессии', 'сессий') + '</p><div class="pre" style="margin:0 0 12px">' +
      esc(r.text) + '</div><div class="btnrow" style="padding:0"><button class="btn pri" data-copy>Скопировать</button>' +
      (ex.contact ? '<a class="btn sec" target="_blank" rel="noopener" href="' + esc(ex.messenger === 'Telegram' ?
        tgLink(ex.contact, r.text) : waLink(ex.contact, r.text)) + '">Отправить</a>' : '') + '</div>');
    $('[data-copy]', sh).onclick = () => copyText(r.text);
  }, () => {}));
  const wrt = $('[data-write]', app);
  if (wrt && ex) wrt.onclick = () => {
    if (ex.contact) window.open(ex.messenger === 'Telegram' ? tgLink(ex.contact, '') : waLink(ex.contact, ''), '_blank');
    else toast('Контакт не указан', true);
  };
  const ar = $('[data-archive]', app);
  if (ar && ex) ar.onclick = () => save({ archived: !ex.archived });
  const cd = $('[data-codes]', app);
  if (cd && ex) cd.onclick = () => promptSheet('Код в календаре', sortCodes(ex.calCodes).join(', '), 'text',
    (v) => save({ calCodes: String(v || '') }),
    'Как в начале названия встречи: ИК, ПК, хПК. Новые клиенты с этим кодом попадут в эту группу. Несколько — через запятую.');
  const dl = $('[data-del]', app);
  if (dl && ex) dl.onclick = () => {
    if (!confirm('Удалить группу «' + ex.name + '»?')) return;
    void call('deleteGroup', ex.id).then(() => afterChange('Группа удалена').then(back), () => {});
  };
  // вид группы: сменить можно, но списания всех её сессий переедут — поэтому спрашиваем
  const kd = $('[data-kind]', app);
  if (kd) kd.onclick = () => chooseSheet<GroupKind>('Вид группы', [{ value: GROUP_KIND.PERSONAL, label: 'Личная группа',
    hint: 'клиенты платят сами, у каждого свой счёт' }, { value: GROUP_KIND.PROJECT, label: 'Коммерческий проект',
    hint: 'за клиентов платит организация, счёт общий' }], g.kind, (v) => {
    if (v === g.kind) return;
    if (!ex) {
      save(v === GROUP_KIND.PROJECT ? { kind: v, meet: MEET.BY_GROUP, pays: 'monthly', payDay: 5,
        typeId: g.typeId || defaultType().id } : { kind: v, meet: g.meet === MEET.BY_GROUP ? '' : g.meet });
      return;
    }
    if (cls.length && !confirm('Сессии группы «' + g.name + '» — и прошлые тоже — будут списываться ' +
      (v === GROUP_KIND.PERSONAL ? 'со счёта каждого клиента' : 'со счёта группы') + '. Клиентов в группе: ' +
      cls.length + '. Продолжить?')) return;
    save({ kind: v });
  });
  $$('[data-f]', app).forEach((b) => {
    b.onclick = () => {
      const f = b.getAttribute('data-f') as 'name' | 'inn' | 'bankNames' | 'contact' | 'note';
      promptSheet($('.k', b).textContent ?? '', g[f], (b.getAttribute('data-type') || 'text') as 'text',
        (v) => save({ [f]: v }));
    };
  });
  const pc = $('[data-price]', app);
  if (pc) pc.onclick = () => promptSheet('Цена за сессию', g.price, 'number', (v) => {
    curSheet('Валюта', g.cur || S.settings.main_currency, (cu) => save({ price: v === '' ? null : Number(v), cur: cu }));
  }, comm ? '' : 'Пусто — цена по типу сессии или своя у клиента');
  const tp = $('[data-type]', app);
  if (tp) tp.onclick = () => chooseSheet('Тип сессии', [{ value: '', label: 'Как у клиента' }, ...activeTypes().map((t) =>
    ({ value: t.id, label: t.name, hint: t.dur + ' мин' }))], g.typeId || '', (v) => save({ typeId: v || null }));
  const py = $('[data-pays]', app);
  if (py) py.onclick = () => chooseSheet('Как платит', [{ value: 'per_session', label: 'За каждую сессию' },
    { value: 'monthly', label: 'Раз в месяц, за прошлый' }], g.pays, (v) => {
    if (v === 'monthly') promptSheet('Число месяца', g.payDay || 5, 'number', (d) => save({ pays: v, payDay: Number(d) || 5 }));
    else save({ pays: v });
  });
  $$('[data-tog]', app).forEach((b) => { b.onclick = () => save({ selfReceipt: !g.selfReceipt }); });
  $('[data-color]', app).onclick = () => {
    const dk = comm ? S.settings.color_project : S.settings.color_personal;
    colorSheet({ title: 'Цвет клиентов группы', current: g.color || '', kind: 'client', none: 'Как у всех',
      noneHint: colName(isColor(dk) ? dk : COLOR_DEFAULTS[comm ? 'project' : 'personal']).toLowerCase() + ' · по умолчанию' },
    (v) => save({ color: v }));
  };
  const mt = $('[data-meet]', app);
  if (mt) mt.onclick = () => {
    const tmp = { meet: g.meet, link: g.link, address: '' };
    meetSheet(tmp, null, () => save({ meet: tmp.meet, link: tmp.link }));
  };
  const ms = $('[data-msgr]', app);
  if (ms) ms.onclick = () => chooseSheet('Мессенджер', MESSENGERS, g.messenger, (v) => save({ messenger: v }));
  $$('[data-rule]', app).forEach((b) => {
    b.onclick = () => {
      const r = b.getAttribute('data-rule');
      if (r === 'hours') promptSheet('Порог «вовремя», часов', rules.hours, 'number', (v) => save({ ruleHours: Number(v) || 24 }));
      else chooseRule(r === 'late' ? 'Отмена поздно, оплачивается' : 'Не пришёл, оплачивается',
        r === 'late' ? g.ruleLate : g.ruleNoShow, g.cur, (v) => save({ [r === 'late' ? 'ruleLate' : 'ruleNoShow']: v }));
    };
  });
  const cr = $('[data-create]', app);
  if (cr) cr.onclick = () => void call<{ id: string }>('saveGroup', g).then((r) => {
    UI.draftGroup = null;
    return afterChange(comm ? 'Проект добавлен' : 'Группа добавлена').then(() => replace('group', { id: r.id }));
  }, () => {});
};
